import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const formatarMinutos = (segundos) => {
  const minutos = Math.ceil(segundos / 60);
  return minutos <= 1 ? '1 minuto' : `${minutos} minutos`;
};

// Verify responde rápido demais (é só uma consulta local) — sem um mínimo de espera, cada
// tentativa pisca na tela instantaneamente, o que parece automação, não uma verificação de
// verdade. Vale pro sucesso e pro erro, os dois.
const ATRASO_MINIMO_MS = 300;

const aguardarAtrasoMinimo = async (inicio) => {
  const decorrido = Date.now() - inicio;
  if (decorrido < ATRASO_MINIMO_MS) {
    await new Promise((resolve) => setTimeout(resolve, ATRASO_MINIMO_MS - decorrido));
  }
};

// O bloqueio é por conta, não por essa tela — se o usuário voltar pro login e tentar de novo,
// o backend já está contando a partir de onde parou. O contador local precisa acompanhar isso,
// senão volta a mostrar "1º erro" para uma tentativa que na verdade já é a 3ª.
const CHAVE_TENTATIVAS_ERRADAS = 'twoFactorTentativasErradas';

const lerTentativasErradasSalvas = () => {
  try {
    return parseInt(sessionStorage.getItem(CHAVE_TENTATIVAS_ERRADAS), 10) || 0;
  } catch {
    return 0;
  }
};

const salvarTentativasErradas = (valor) => {
  try {
    sessionStorage.setItem(CHAVE_TENTATIVAS_ERRADAS, String(valor));
  } catch {
    // sessionStorage indisponível (modo privado etc.) — só perde a persistência do contador.
  }
};

const limparTentativasErradas = () => {
  try {
    sessionStorage.removeItem(CHAVE_TENTATIVAS_ERRADAS);
  } catch {
    // idem
  }
};

const LoginTwoFactor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { verifyTwoFactor } = useAuth();

  const twoFactorToken = location.state?.twoFactorToken;

  const [usaRecuperacao, setUsaRecuperacao] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [codigoRecuperacao, setCodigoRecuperacao] = useState('');
  const [erro, setErro] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [retryAfterSec, setRetryAfterSec] = useState(null);
  // O aviso de tentativas restantes só aparece a partir do 2º erro — no primeiro, "código
  // inválido" já basta, e a contagem regressiva logo de cara passaria alarmista sem necessidade.
  // Persistido em sessionStorage: o bloqueio é por conta, então voltar pro login e tentar de
  // novo não deve reiniciar essa contagem.
  const [tentativasErradas, setTentativasErradas] = useState(lerTentativasErradasSalvas);
  const inputRef = useRef(null);

  // Sem desafio ativo (ex.: chegou aqui direto pela URL, ou já verificou e voltou), volta pro
  // login sem tela de erro — não é um problema, é só não ter o que verificar.
  useEffect(() => {
    if (!twoFactorToken) {
      navigate('/login', { replace: true });
    }
  }, [twoFactorToken, navigate]);

  useEffect(() => {
    if (!usaRecuperacao) {
      inputRef.current?.focus();
    }
  }, [usaRecuperacao]);

  useEffect(() => {
    if (retryAfterSec === null || retryAfterSec <= 0) return undefined;
    const intervalo = setInterval(() => {
      setRetryAfterSec((atual) => (atual === null ? null : Math.max(0, atual - 1)));
    }, 1000);
    return () => clearInterval(intervalo);
  }, [retryAfterSec]);

  const confirmar = async (valores) => {
    setErro('');
    setVerificando(true);
    const inicio = Date.now();

    try {
      await verifyTwoFactor(twoFactorToken, valores);
      await aguardarAtrasoMinimo(inicio);
      limparTentativasErradas();
      navigate('/dashboard');
    } catch (err) {
      await aguardarAtrasoMinimo(inicio);

      if (err.code === 'TWO_FACTOR_TOKEN_EXPIRED') {
        navigate('/login', {
          replace: true,
          state: { message: 'Sua sessão de login expirou. Entre de novo.' },
        });
        return;
      }

      if (err.code === 'TWO_FACTOR_RATE_LIMITED') {
        setRetryAfterSec(err.retryAfterSeconds || 60);
        setErro(`Muitas tentativas. Tente novamente em ${formatarMinutos(err.retryAfterSeconds || 60)}.`);
        setCodigo('');
        setCodigoRecuperacao('');
        setVerificando(false);
        return;
      }

      const numeroDaTentativa = tentativasErradas + 1;
      setTentativasErradas(numeroDaTentativa);
      salvarTentativasErradas(numeroDaTentativa);

      const aviso =
        err.attemptsRemaining !== undefined && numeroDaTentativa >= 2
          ? ` ${err.attemptsRemaining === 1 ? 'Falta 1 tentativa' : `Faltam ${err.attemptsRemaining} tentativas`} antes de bloquear a conta.`
          : '';

      if (err.code === 'RECOVERY_CODE_INVALID') {
        setErro(`Código de recuperação inválido ou já utilizado.${aviso}`);
      } else {
        setErro(`Código inválido. Confira o app autenticador e tente novamente.${aviso}`);
      }

      setCodigo('');
      setCodigoRecuperacao('');
      setVerificando(false);
    }
  };

  const handleCodigoChange = (event) => {
    const digitos = event.target.value.replace(/\D/g, '').slice(0, 6);
    setCodigo(digitos);
    if (digitos.length === 6 && !verificando) {
      confirmar({ code: digitos });
    }
  };

  const handleRecuperacaoSubmit = (event) => {
    event.preventDefault();
    if (!codigoRecuperacao.trim() || verificando) return;
    confirmar({ recoveryCode: codigoRecuperacao.trim().toUpperCase() });
  };

  const alternarParaRecuperacao = () => {
    setUsaRecuperacao(true);
    setErro('');
    setCodigo('');
  };

  const alternarParaCodigo = () => {
    setUsaRecuperacao(false);
    setErro('');
    setCodigoRecuperacao('');
  };

  if (!twoFactorToken) {
    return null;
  }

  const travado = retryAfterSec !== null && retryAfterSec > 0;

  return (
    <div className="bg-[#012B87] bg-fixed min-h-screen">
      <div className="max-w-[392px] mx-auto h-[90vh] flex items-center flex-col justify-center px-4 lg:px-0">
        <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11">
          <div className="flex items-center gap-3 mb-4">
            <i className="bi-shield-lock text-2xl text-slate-700"></i>
            <h1 className="text-lg md:text-xl font-semibold">Verificação em duas etapas</h1>
          </div>

          {erro ? (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
              {erro}
              {travado ? (
                <div className="mt-1 font-semibold">Tempo restante: {formatarMinutos(retryAfterSec)}</div>
              ) : null}
            </div>
          ) : null}

          {!usaRecuperacao ? (
            <>
              <p className="text-xs mb-5 text-gray-600">
                Digite o código de 6 dígitos do seu app autenticador.
              </p>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="w-full text-center text-2xl tracking-[0.5em] border border-gray-300 rounded py-3 outline-none focus:border-[#2C1944] disabled:opacity-60"
                value={codigo}
                onChange={handleCodigoChange}
                disabled={verificando || travado}
                maxLength={6}
              />
              <div className="mt-5 text-center text-xs">
                <button
                  type="button"
                  onClick={alternarParaRecuperacao}
                  className="text-sky-500 underline disabled:opacity-60"
                  disabled={verificando}
                >
                  Usar um código de recuperação
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleRecuperacaoSubmit} className="flex flex-col gap-4">
              <p className="text-xs text-gray-600">
                Digite um dos códigos de recuperação que você guardou quando ativou a verificação em
                duas etapas.
              </p>
              <input
                type="text"
                placeholder="XXXX-XXXX"
                className="w-full text-center text-lg tracking-widest border border-gray-300 rounded py-3 outline-none focus:border-[#2C1944] uppercase disabled:opacity-60"
                value={codigoRecuperacao}
                onChange={(event) => setCodigoRecuperacao(event.target.value)}
                disabled={verificando || travado}
              />
              <input
                type="submit"
                value={verificando ? 'Verificando...' : 'Confirmar'}
                className="submitBtn transition-all w-full rounded py-2 cursor-pointer font-semibold bg-tBlue hover:bg-tBlue2 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={verificando || travado || !codigoRecuperacao.trim()}
              />
              <button
                type="button"
                onClick={alternarParaCodigo}
                className="text-sky-500 underline text-xs disabled:opacity-60"
                disabled={verificando}
              >
                Usar o código do app autenticador
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginTwoFactor;
